import React, { useMemo } from 'react';

const getRangeLabel = (takeaway) => {
  const label = takeaway.concept || takeaway.takeaway || 'Takeaway segment';
  const start = Math.round(takeaway.start_seconds || 0);
  const end = Math.round(takeaway.end_seconds || 0);

  return `${label} from ${start} to ${end} seconds`;
};

const TakeawayTimeline = ({
  duration,
  currentTime,
  takeaways,
  onSeek,
  showBubble = true,
}) => {
  const activeTakeaway = useMemo(() => {
    if (!takeaways || !takeaways.length || !duration) {
      return null;
    }

    return (
      takeaways.find(
        (takeaway) =>
          typeof takeaway.start_seconds === 'number' &&
          typeof takeaway.end_seconds === 'number' &&
          currentTime >= takeaway.start_seconds &&
          currentTime <= takeaway.end_seconds,
      ) || null
    );
  }, [currentTime, duration, takeaways]);

  const startPercent = useMemo(() => {
    if (!activeTakeaway || !duration) return 0;
    const start = activeTakeaway.start_seconds || 0;
    return (Math.max(start, 0) / duration) * 100;
  }, [activeTakeaway, duration]);

  if (!duration || !takeaways || takeaways.length === 0) {
    return null;
  }

  const progressPercent = Math.min((currentTime / duration) * 100, 100);

  return (
    <div className="w-full space-y-2">
      <div className="relative h-3 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="absolute inset-y-0 w-1 bg-red-500 z-20 pointer-events-none"
          style={{
            left: `${progressPercent}%`,
            transform: 'translateX(-50%)'
          }}
        />
        {takeaways.map((takeaway, index) => {
          if (
            typeof takeaway.start_seconds !== 'number' ||
            typeof takeaway.end_seconds !== 'number' ||
            takeaway.end_seconds <= takeaway.start_seconds
          ) {
            return null;
          }

          const startPercent =
            (Math.max(takeaway.start_seconds, 0) / duration) * 100;
          const widthPercent = Math.max(
            ((takeaway.end_seconds - takeaway.start_seconds) / duration) * 100,
            0,
          );
          const label = getRangeLabel(takeaway);

          return (
            <button
              key={`${takeaway.concept || takeaway.takeaway || 'takeaway'}-${index}`}
              type="button"
              className={`absolute top-0 h-full transition-all duration-200 ${activeTakeaway === takeaway
                ? 'bg-emerald-600 z-10'
                : 'bg-emerald-400/40 hover:bg-emerald-500'
                } focus:outline-none focus:ring-2 focus:ring-emerald-600`}
              style={{ left: `${startPercent}%`, width: `${widthPercent}%` }}
              onClick={() => onSeek && onSeek(takeaway.start_seconds)}
              aria-label={`Jump to ${label}`}
            />
          );
        })}
      </div>
      {showBubble && activeTakeaway && (
        <div className="relative pt-2">
          <div className="flex w-full pointer-events-none">
            <div
              style={{
                flex: `0 1 ${startPercent}%`,
                minWidth: 0,
                transition: 'flex-basis 0.3s ease-out'
              }}
            />
            <div
              className="relative flex-none"
              style={{ maxWidth: '80%' }}
            >
              <div className="inline-block rounded-xl border border-gray-200 bg-white px-4 py-2 pointer-events-auto">
                <div className="whitespace-normal break-words">
                  {(() => {
                    const text = activeTakeaway.concept || activeTakeaway.takeaway;
                    const colonIndex = text.indexOf(':');
                    if (colonIndex !== -1) {
                      return (
                        <>
                          <span className="font-semibold">{text.slice(0, colonIndex)}</span>
                          <span>{text.slice(colonIndex)}</span>
                        </>
                      );
                    }
                    return <span className="font-semibold">{text}</span>;
                  })()}
                </div>
                {activeTakeaway.example && (
                  <div className="text-sm text-gray-700 mt-1">{activeTakeaway.example}</div>
                )}
              </div>
            </div>
          </div>
          <div
            className="absolute -top-1 h-2 w-2 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-white z-10 transition-all duration-300 ease-out"
            style={{ left: `${startPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default TakeawayTimeline;
